import {
  type PersonalizationSupportLevel,
  personalizationCompareRows,
} from '@/content/platform/personalization';

interface CompetitorCol {
  key: 'radium' | 'optimizely' | 'adobeTarget' | 'dynamicYield' | 'mutiny';
  label: string;
  accent?: boolean;
}

const COMPETITORS: CompetitorCol[] = [
  { key: 'radium', label: 'Radium', accent: true },
  { key: 'optimizely', label: 'Optimizely' },
  { key: 'adobeTarget', label: 'Adobe Target' },
  { key: 'dynamicYield', label: 'Dynamic Yield' },
  { key: 'mutiny', label: 'Mutiny' },
];

function SupportIcon({ level }: { level: PersonalizationSupportLevel }) {
  const ch = level === 'full' ? '✓' : level === 'partial' ? '~' : '×';
  const color =
    level === 'full'
      ? 'var(--accent)'
      : level === 'partial'
        ? 'var(--warning)'
        : 'var(--ink-muted)';
  return (
    <span
      className="text-mono"
      role="img"
      style={{ fontSize: 16, color, fontWeight: 600 }}
      aria-label={level}
    >
      {ch}
    </span>
  );
}

/**
 * "Built for the post A/B test era." — deck slide 9.
 * Personalization comparison vs Optimizely / Adobe Target / Dynamic Yield / Mutiny.
 */
export function PersonalizationCompareTable() {
  return (
    <div
      className="compare-table-wrap"
      style={{ overflowX: 'auto', position: 'relative' }}
    >
      <table
        style={{
          width: '100%',
          minWidth: 720,
          borderCollapse: 'collapse',
          fontSize: 14,
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                textAlign: 'left',
                padding: 12,
                borderBottom: '1px solid var(--border-subtle)',
                color: 'var(--ink-muted)',
                fontWeight: 500,
              }}
            >
              Capability
            </th>
            {COMPETITORS.map((c) => (
              <th
                key={c.key}
                style={{
                  padding: 12,
                  borderBottom: '1px solid var(--border-subtle)',
                  color: c.accent ? 'var(--accent)' : 'var(--ink-muted)',
                  fontWeight: c.accent ? 600 : 500,
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {personalizationCompareRows.map((row) => (
            <tr key={row.capability}>
              <td
                style={{
                  padding: '14px 12px',
                  borderBottom: '1px solid var(--border-subtle)',
                  color: 'var(--ink-primary)',
                  fontWeight: 500,
                }}
              >
                {row.capability}
              </td>
              {COMPETITORS.map((c) => (
                <td
                  key={c.key}
                  style={{
                    textAlign: 'center',
                    borderBottom: '1px solid var(--border-subtle)',
                    background: c.accent ? 'rgba(0,218,165,0.04)' : undefined,
                  }}
                >
                  <SupportIcon
                    level={
                      row[c.key as keyof typeof row] as PersonalizationSupportLevel
                    }
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p
        className="text-mono"
        style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 16 }}
      >
        ✓ full · ~ partial · × none
      </p>
    </div>
  );
}
